import { useEffect, useMemo } from "react"
import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import CircleIcon from "@mui/icons-material/Circle"
import { useTheme } from "@mui/material"

import usePermissions from "helpers/hooks/usePermissions"
import { useDeviceGroupQuery } from "features/api"
import PreviousPage from "widgets/common/PreviousPage"
import Spinner from "widgets/common/Spinner"
import PageTitle from "widgets/common/PageTitle"
import EditableDeviceGroupName from "widgets/deviceGroups/editor/EditableDeviceGroupName"
import useRoutes from "helpers/hooks/useRoutes"
import { getColorFromString } from "helpers/utils/styles"
import LinkTabsSection from "widgets/common/LinkTabsSection"
import { getGroupTabsConfig } from "helpers/config/tabs/groupTabsConfig"
import EntityTag from "widgets/common/EntityTag"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"
import useDevices from "helpers/hooks/useDevices"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import { groupHasTriggeredAlerts } from "helpers/utils/alerts"

function DeviceGroup() {
  const theme = useTheme()
  const { t } = useTranslation()
  const { baseOrgURL } = useRoutes()
  const { isEditor } = usePermissions()
  const navigate = useNavigate()
  const { id } = useParams()
  const {
    currentData: deviceGroup,
    isFetching: deviceGroupFetching,
    isSuccess: deviceGroupSuccess,
    isError,
  } = useDeviceGroupQuery({
    id: Number(id),
    params: {},
  })

  useDocumentTitle(deviceGroup?.name ?? t("pageTitles.DEVICE_GROUP"))

  const groupColor = useMemo(
    () => getColorFromString(deviceGroup?.name ?? "", theme.palette),
    [deviceGroup, theme],
  )
  const groupTabsConfig = useMemo(() => getGroupTabsConfig(), [])

  const { devsWRelsById } = useDevices()
  const { orgDevicesAlertSettingsWStates } = useAlertSettingsWStates()
  const { orgActiveDevicesAlertSettingsById } = useAlertSettings()

  const hasTriggeredAlerts = useMemo(
    () =>
      groupHasTriggeredAlerts(
        orgDevicesAlertSettingsWStates,
        orgActiveDevicesAlertSettingsById,
        devsWRelsById,
        Number(id),
      ),
    [
      orgDevicesAlertSettingsWStates,
      orgActiveDevicesAlertSettingsById,
      devsWRelsById,
      id,
    ],
  )

  useEffect(() => {
    if (isError) {
      navigate(`${baseOrgURL}/device-groups`)
    }
  }, [isError, navigate, baseOrgURL])

  return (
    <>
      {deviceGroupFetching && !deviceGroup ? (
        <Spinner styles={{ my: 2 }} />
      ) : (
        <>
          <PreviousPage href={`${baseOrgURL}/device-groups`} />
          {deviceGroupSuccess && deviceGroup && (
            <>
              <Stack
                flexDirection={"row"}
                columnGap={"15px"}
                rowGap={"5px"}
                alignItems={"center"}
                marginBottom={"0.5rem"}
                flexWrap={"wrap"}
              >
                <EntityTag entity={"group"} />
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  <CircleIcon sx={{ fontSize: "0.7rem", color: groupColor }} />
                  {isEditor ? (
                    <EditableDeviceGroupName name={deviceGroup.name} />
                  ) : (
                    <PageTitle text={deviceGroup.name} styles={{ p: 0 }} />
                  )}
                </Stack>
              </Stack>
              <LinkTabsSection
                tabsConfig={groupTabsConfig}
                badges={{ "tabs.ALERTS": hasTriggeredAlerts }}
              />
              <Outlet context={{ deviceGroup, deviceGroupFetching }} />
            </>
          )}
        </>
      )}
    </>
  )
}

export default DeviceGroup
